import type { CurateBlock, ResponseType } from '@/api/curate';

/**
 * Payload readers and page grouping for Curate content blocks.
 *
 * `CurateBlock.payload` is `Record<string, unknown>` by design: the shape is
 * Kai-Studio's contract and varies per `blockType`. Rather than assert a union
 * this app cannot enforce, every field is read through a narrowing helper that
 * returns a safe default. A malformed or future block therefore degrades to
 * empty content instead of throwing inside a render and blanking the reader.
 */

export const str = (payload: Record<string, unknown>, key: string): string => {
  const v = payload[key];
  return typeof v === 'string' ? v : '';
};

export const num = (payload: Record<string, unknown>, key: string, fallback: number): number => {
  const v = payload[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
};

export const strList = (payload: Record<string, unknown>, key: string): string[] => {
  const v = payload[key];
  return Array.isArray(v) ? v.filter((i): i is string => typeof i === 'string') : [];
};

export interface QuestionOption {
  label: string;
  /**
   * Present in the payload, but NEVER used to render feedback. Grading is
   * server-side: the client sends selections and reads `isCorrect` off the
   * saved response. Rendering from this flag would both duplicate the grading
   * rule and leak the answer key to anyone reading the network payload.
   */
  feedback: string;
}

export interface QuestionPayload {
  prompt: string;
  responseType: ResponseType;
  options: QuestionOption[];
}

const RESPONSE_TYPES: ResponseType[] = ['single_choice', 'multi_select', 'free_text'];

/**
 * Read a question block's payload.
 *
 * Falls back to `single_choice` on an unrecognized `response_type`, since an
 * unknown type with options still renders usefully as a choice list, whereas
 * throwing would take out the whole page.
 */
export const readQuestion = (payload: Record<string, unknown>): QuestionPayload => {
  const rawType = str(payload, 'response_type') as ResponseType;
  const responseType = RESPONSE_TYPES.includes(rawType) ? rawType : 'single_choice';

  const rawOptions = Array.isArray(payload.options) ? payload.options : [];
  const options: QuestionOption[] = rawOptions.map((o) => {
    const opt = (o ?? {}) as Record<string, unknown>;
    return {
      label: typeof opt.label === 'string' ? opt.label : '',
      feedback: typeof opt.feedback === 'string' ? opt.feedback : '',
    };
  });

  return { prompt: str(payload, 'prompt'), responseType, options };
};

/**
 * Split blocks into pages.
 *
 * The API precomputes `pageIndex` at import by counting preceding `page_break`
 * blocks, so grouping on it is authoritative and this never needs to re-derive
 * page boundaries. `page_break` blocks themselves are dropped: they are
 * separators, not content.
 *
 * Returns at least one (possibly empty) page so callers can index [0] without
 * a guard. Gaps in `pageIndex` are tolerated — a missing index yields an empty
 * page rather than shifting subsequent content onto the wrong page, which
 * would silently desynchronize saved resume positions.
 */
export const groupBlocksByPage = (blocks: CurateBlock[]): CurateBlock[][] => {
  const content = blocks.filter((b) => b.blockType !== 'page_break');
  if (content.length === 0) return [[]];

  const highest = content.reduce((max, b) => Math.max(max, b.pageIndex ?? 0), 0);
  const pages: CurateBlock[][] = Array.from({ length: highest + 1 }, () => []);

  for (const block of content) {
    const idx = Number.isInteger(block.pageIndex) && block.pageIndex >= 0 ? block.pageIndex : 0;
    pages[idx].push(block);
  }

  for (const page of pages) page.sort((a, b) => a.position - b.position);
  return pages;
};

/** Question blocks on a page, in reading order. */
export const questionsOnPage = (page: CurateBlock[]): CurateBlock[] =>
  page.filter((b) => b.blockType === 'question');
